import { useRender } from "@base-ui/react/use-render";
import { cn } from "@simble/shared/lib/cn";
import { AnimatePresence, motion } from "motion/react";
import React from "react";

interface ButtonProps extends React.ComponentProps<"button"> {
  asChild?: boolean;
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  render?: React.ReactElement;
  theme?: "primary" | "text" | "outline";
}

const Button = ({ isLoading, disabled, theme = "primary", children, className, leftIcon, asChild = false, render, ...rest }: ButtonProps) => {
  // Для обратной совместимости: если asChild=true, используем первый дочерний элемент как render
  const renderElement: React.ReactElement<Record<string, unknown>> | undefined = React.useMemo(() => {
    if (render) {
      return render as React.ReactElement<Record<string, unknown>>;
    }
    if (asChild) {
      // Если asChild=true, берем первый дочерний элемент как render
      const firstChild = React.Children.toArray(children)[0];
      if (React.isValidElement(firstChild)) {
        return firstChild as React.ReactElement<Record<string, unknown>>;
      }
    }
    return;
  }, [render, asChild, children]);

  const buttonContent = (
    <AnimatePresence initial={false} mode="wait">
      {isLoading ? (
        <motion.span
          animate={{ opacity: 1, scale: 1 }}
          className="flex items-center justify-center gap-2"
          exit={{ opacity: 0, scale: 0.97 }}
          initial={{ opacity: 0, scale: 0.97 }}
          key="loader"
          transition={{ duration: 0.2 }}
        >
          <span aria-hidden className="box-border size-5 shrink-0 animate-spin rounded-full border-2 border-white/32 border-t-white border-solid" />
        </motion.span>
      ) : (
        <motion.span
          animate={{ opacity: 1, scale: 1 }}
          className="flex items-center justify-center gap-2"
          exit={{ opacity: 0, scale: 0.97 }}
          initial={{ opacity: 0, scale: 0.97 }}
          key="content"
          transition={{ duration: 0.2 }}
        >
          {leftIcon}
          {!asChild && children}
        </motion.span>
      )}
    </AnimatePresence>
  );

  // Если используется render/asChild, нужно добавить isLoading и leftIcon в children render элемента
  const finalRenderElement = React.useMemo((): React.ReactElement<Record<string, unknown>> | undefined => {
    if (!renderElement) {
      return;
    }

    // Если есть isLoading или leftIcon, нужно обернуть children render элемента
    if (isLoading || leftIcon) {
      return React.cloneElement(renderElement, {
        children: (
          <>
            {!!isLoading && (
              <span aria-hidden className="box-border size-6 shrink-0 animate-spin rounded-full border border-white/32 border-t-white border-solid" />
            )}
            {leftIcon}
            {renderElement.props.children}
          </>
        ),
      });
    }

    return renderElement;
  }, [renderElement, isLoading, leftIcon]);

  const renderParams: Parameters<typeof useRender>[0] = {
    defaultTagName: "button",
    props: {
      className: cn(getThemeClasses(theme), isLoading ? "bg-sber" : "", className),
      disabled: disabled || isLoading,
      type: finalRenderElement ? undefined : "button",
      ...rest,
      children: finalRenderElement ? undefined : buttonContent,
    },
  };

  if (finalRenderElement) {
    renderParams.render = finalRenderElement;
  }

  return useRender(renderParams);
};

const getThemeClasses = (theme: ButtonProps["theme"]) => {
  switch (theme) {
    case "text":
      return "bg-transparent cursor-pointer text-sber transition-colors button disabled:bg-gray-300 transition-colors disabled:cursor-not-allowed whitespace-nowrap py-4 outline-none flex flex-nowrap items-center gap-3 justify-start";
    default:
      return "cursor-pointer rounded-xl bg-sber px-4 py-4 text-white flex items-center justify-center transition-colors gap-2 disabled:cursor-not-allowed button";
  }
};

export default Button;
